import { createContext, useContext } from "react";
import { Navigate, type NavigateProps } from "react-router-dom";

/**
 * Per-request info threaded from the SSR server into the React tree, so
 * host-dependent rendering (RootRoute, public pages) works on the server where
 * `window` doesn't exist. On the client this context is absent and callers fall
 * back to `window.location`.
 */
export interface SsrRequest {
  url: string;
  host: string;
  /** The request's Referer header, if any. */
  referrer?: string;
}

export const SsrContext = createContext<SsrRequest | null>(null);

export const useSsrRequest = () => useContext(SsrContext);

/** The page that linked here: the Referer header on the server, document.referrer on the client ("" if none). */
export const useReferrer = (): string => {
  const ssr = useContext(SsrContext);
  if (ssr) return ssr.referrer ?? "";
  return typeof document !== "undefined" ? document.referrer : "";
};

/** The current hostname — from the SSR request on the server, `window` on the client. */
export const useHostname = (): string => {
  const ssr = useContext(SsrContext);
  if (ssr) return ssr.host;
  return typeof window !== "undefined" ? window.location.hostname : "";
};

/**
 * A holder the server render passes down so an initial-render redirect can be
 * turned into a real HTTP 302 (React Router's <Navigate> is a no-op on the
 * initial render under <StaticRouter>). Absent on the client.
 */
export interface SsrRedirect {
  to?: string;
  /** 301 for a page that has moved for good (e.g. an old blog post now covered by a guide); 302 by default. */
  status?: 301 | 302;
}

export const SsrRedirectContext = createContext<SsrRedirect | null>(null);

/**
 * Like <Navigate>, but SSR-aware: on the server it records the target so the
 * Express layer can respond with a 302 (301 with `permanent`) instead of shipping an empty page; on
 * the client it behaves as an ordinary <Navigate>.
 */
export const SsrNavigate = ({ to, permanent, ...rest }: NavigateProps & { permanent?: boolean }) => {
  const redirect = useContext(SsrRedirectContext);
  if (redirect && typeof to === "string") {
    redirect.to = to;
    if (permanent) redirect.status = 301;
    return null;
  }
  return <Navigate to={to} {...rest} />;
};

/**
 * A holder the server render passes down so a route can set the HTTP status of the
 * response (e.g. NotFound → 404, so an unknown URL isn't indexed as a "soft 404").
 * Absent on the client.
 */
export interface SsrResponse {
  status?: number;
}

export const SsrResponseContext = createContext<SsrResponse | null>(null);

/** Set the server response status during SSR; a no-op on the client. */
export const useSsrStatus = (status: number) => {
  const res = useContext(SsrResponseContext);
  if (res) res.status = status;
};
